import { useEffect, useState } from "react"
import { t } from "@lingui/core/macro"
import {
    type FollowResponse,
    useFollowMutation,
    useIsFollowingQuery,
    useUnfollowMutation,
} from "../../../../store/followSlice"
import { useAppSelector } from "../../../../store/hooks"
import { mergeClasses } from "../../../../utils/css"
import { error } from "../../../../utils/debug"
import Star from "../../atoms/Icons/Chat/Star"
import styles from "./FollowStar.module.scss"

interface FollowStarProps {
    username: string
    afterFollow?: (username: string, response: FollowResponse) => void
    afterUnfollow?: (username: string, response: FollowResponse) => void
    handleLoggedOut?: (username: string) => void
    initialFollowingState?: boolean
    size?: number
    onFail?: () => void
}

export const FollowStar = ({
    username,
    afterFollow,
    afterUnfollow,
    handleLoggedOut,
    initialFollowingState,
    size = 24,
    onFail,
}: FollowStarProps) => {
    const [localFollowing, setLocalFollowing] = useState<boolean>(
        initialFollowingState ?? false,
    )
    const [isUpdating, setIsUpdating] = useState(false)
    const [pendingFollowing, setPendingFollowing] = useState<boolean | null>(
        null,
    )
    const loggedInUser = useAppSelector((state) => state.user.loggedInUser)
    const shouldSkipQuery =
        !loggedInUser ||
        (initialFollowingState !== undefined && pendingFollowing === null)

    const { data: followStatus, isLoading } = useIsFollowingQuery(username, {
        skip: shouldSkipQuery,
    })

    useEffect(() => {
        if (followStatus?.following !== undefined) {
            setLocalFollowing(followStatus.following)
        }
    }, [followStatus])

    const [follow] = useFollowMutation()
    const [unfollow] = useUnfollowMutation()

    useEffect(() => {
        // sync ui once update is confirmed
        if (pendingFollowing !== null && localFollowing === pendingFollowing) {
            setPendingFollowing(null)
            setIsUpdating(false)
        }
    }, [localFollowing, pendingFollowing])

    const effectiveFollowing =
        pendingFollowing !== null
            ? pendingFollowing
            : (followStatus?.following ?? localFollowing)

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        e.preventDefault()

        if (!loggedInUser) {
            handleLoggedOut?.(username)
            return
        }

        if (isUpdating || isLoading) return

        const newFollowState = !effectiveFollowing
        setPendingFollowing(newFollowState)
        setIsUpdating(true)

        if (!newFollowState) {
            unfollow({ username: username, location: "FollowStar" })
                .unwrap()
                .then((response) => {
                    afterUnfollow?.(username, response)
                })
                .catch((err) => {
                    error("Failed to update follow status:", err)
                    setPendingFollowing(null)
                    setIsUpdating(false)
                    onFail?.()
                })
        } else {
            follow({ username: username, location: "FollowStar" })
                .unwrap()
                .then((response) => {
                    afterFollow?.(username, response)
                })
                .catch((err) => {
                    error("Failed to update follow status:", err)
                    setPendingFollowing(null)
                    setIsUpdating(false)
                    onFail?.()
                })
        }
    }

    return (
        <div
            onClick={handleClick}
            className={mergeClasses(
                styles.followStar,
                isUpdating ? styles.updating : "",
                effectiveFollowing ? styles.following : "",
            )}
            title={
                isUpdating || isLoading
                    ? ""
                    : effectiveFollowing
                      ? t`Unfollow`
                      : t`Follow`
            }
            data-testid="follow-star"
        >
            <Star width={size} height={size} />
        </div>
    )
}
